<script lang="ts">
  // SearchBox — the ONE shared list-search input (wiki/feature-list-search.md
  // §2). It renders the query a `listSearch` handle holds and calls `setQuery`;
  // it owns no filtering. Composed from the design-system TextField + IconButton
  // — no bespoke field chrome. Typed component props, never slots.
  //
  // The result count rides a polite live region, so it is announced on the
  // DEBOUNCED result (`search.results` only moves on the debounce) and never
  // per keystroke.
  import { X } from '$lib/ui/icons';
  import IconButton from '../primitives/IconButton.svelte';
  import TextField from '../primitives/TextField.svelte';
  import { t } from '$lib/i18n';
  import { UiKeys } from '@voicedojo/backend-types';
  import type { ListSearch } from './listSearch.svelte';

  interface Props {
    /** The list's search handle — the only state this component reads or drives. */
    search: ListSearch<unknown>;
    /** Test hook. */
    'data-testid'?: string;
  }

  // eslint-plugin-svelte 3.23.0 `no-unused-props` misses a quoted prop key whose
  // destructure carries a default: getUsedPropertyNamesFromPattern reads a Literal
  // key only when the value is a bare Identifier, never an AssignmentPattern. The
  // default lives at the use site until that is fixed upstream.
  let { search, 'data-testid': dataTestid }: Props = $props();
  const testId = $derived(dataTestid ?? 'list-search');

  const count = $derived(search.results.length);
</script>

<div class="vd-search">
  <TextField
    class="vd-search__field"
    label={t(UiKeys.Search.Label)}
    type="search"
    placeholder={t(UiKeys.Search.Placeholder)}
    value={search.query}
    onInput={(v) => search.setQuery(v)}
    onEscape={() => search.clear()}
    data-testid={testId}
  />
  {#if search.query !== ''}
    <IconButton
      label={t(UiKeys.Search.Clear)}
      title={t(UiKeys.Search.Clear)}
      icon={X}
      size="sm"
      onclick={() => search.clear()}
      data-testid="{testId}-clear"
    />
  {/if}
  <p class="visually-hidden" aria-live="polite" data-testid="{testId}-count">
    {#if search.active}
      {t(count === 1 ? UiKeys.Search.ResultsOne : UiKeys.Search.ResultsMany, { Count: count })}
    {/if}
  </p>
</div>

<style lang="scss">
  .vd-search {
    display: flex;
    align-items: flex-end;
    gap: 0.5rem;
    max-width: 32rem;
  }

  .vd-search :global(.vd-search__field) {
    flex: 1 1 auto;
    min-width: 0;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
</style>
